import { CheckIcon } from 'lucide-react';
import { type ChangeEvent, type MouseEvent, useId } from 'react';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { mergeStyling } from '../../common-functions';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import ErrorLabel from '../../labels/error-label';
import { FieldDescription } from '../../labels/field-description';

export interface CheckboxChangeEventType {
  name?: string;
  value: unknown;
  checked: boolean;
}

export interface CheckboxProps extends Omit<DynamicFieldComponentProps<unknown, CheckboxChangeEventType>, 'onChange'> {
  checked?: boolean;
  /**
   * Helper text rendered under the label. Wired to the input via aria-describedby so screen
   * readers announce it as a description rather than part of the accessible name.
   */
  description?: string;
  /**
   * Mark the field as invalid for styling and aria-invalid without rendering an inline error
   * message. Use this when a parent component owns the error message (e.g. a checkbox group).
   * If you have a string error message, pass it via `error` instead.
   */
  invalid?: boolean;
  /**
   * Accessible name for the checkbox when no visible `label` is provided (e.g. table row/select-all
   * checkboxes). Ignored if `label` is set, which takes precedence via aria-labelledby.
   */
  ariaLabel?: string;
  /**
   * Fires on every user-driven change with a defined event.
   */
  onChange?: (value: CheckboxChangeEventType) => void;
}

export const Checkbox = ({
  name,
  label,
  value,
  checked,
  description,
  invalid,
  ariaLabel,
  onChange,
  readOnly,
  required,
  error,
  disabled,
}: CheckboxProps) => {
  const isInteractive = !readOnly && !disabled;
  const isInvalid = !!error || !!invalid;
  const reactId = useId();
  const labelId = `${reactId}-label`;
  const descriptionId = `${reactId}-description`;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isInteractive) return;
    onChange?.({ name, value, checked: event.target.checked });
  };

  // HTML `readonly` doesn't actually prevent checkbox toggling. Block the click so the input
  // doesn't change state in uncontrolled mode (controlled mode is also covered as a side effect).
  // Keeps the input focusable, unlike `disabled`.
  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    if (readOnly) {
      event.preventDefault();
    }
  };

  const isRequiredIndicatorVisible = required === DynamicFormRequiredType.True && isInteractive;

  return (
    <div className="flex flex-col gap-2">
      <div className={mergeStyling(isRequiredIndicatorVisible && 'flex items-center gap-2')}>
        <label
          className={mergeStyling(
            'flex w-fit select-none gap-2 text-sm',
            description ? 'items-start' : 'items-center',
            isInteractive ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
          )}
        >
          <input
            type="checkbox"
            name={name}
            checked={checked}
            disabled={disabled}
            readOnly={readOnly}
            onChange={handleChange}
            onClick={handleClick}
            aria-label={!label && ariaLabel ? ariaLabel : undefined}
            aria-labelledby={label ? labelId : undefined}
            aria-describedby={description ? descriptionId : undefined}
            aria-invalid={isInvalid || undefined}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={mergeStyling(
              'relative inline-flex size-4 shrink-0 items-center justify-center',
              'rounded-[4px] border bg-mono-1 outline-none transition-colors',
              description && 'mt-0.5',
              isInvalid ? 'border-error ring-2 ring-error/20' : 'border-gray-4',
              'peer-checked:border-primary-2 peer-checked:bg-primary-2',
              'peer-checked:[&>svg]:opacity-100',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-2'
            )}
          >
            <CheckIcon className="size-3.5 text-white opacity-0" />
          </span>
          {(label || description) && (
            <div className="flex flex-col gap-1">
              {label && (
                <span id={labelId} className={mergeStyling(isInvalid && 'text-error')}>
                  {label}
                </span>
              )}
              {description && <FieldDescription id={descriptionId}>{description}</FieldDescription>}
            </div>
          )}
        </label>
        <RequiredIndicator readOnly={!isInteractive} required={required} />
      </div>
      {error && (
        <ErrorLabel role="alert" className="mt-0">
          {error}
        </ErrorLabel>
      )}
    </div>
  );
};
